import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { CanonicalCut, IntegerProblem } from '../core/cuts'
import { CutService } from '@/game/cut-service'
import { PolyhedronManager } from '@/graphics/polyhedron-manager'
import { BnBManager, type BnBNode } from './bnb-manager'
import { uiManager, CutHistoryItem } from './ui-manager'
import {
  clearBnBCaches as clearBnBCachesController,
  clearBranchOverlays as clearBranchOverlaysController,
  renderBnBNode as renderBnBNodeController,
  setupBnBIntegration as setupBnBIntegrationController,
  type BnBVisualizationEngineContext,
} from './engine/bnb-visualization-controller'
import {
  applyCut as applyCutController,
  cancelCut as cancelCutController,
  cleanupCuttingPlane as cleanupCuttingPlaneController,
  confirmCut as confirmCutController,
  createCutWithMuValue as createCutWithMuValueController,
  executeCut as executeCutController,
  handleCutMuChange as handleCutMuChangeController,
  hideCutConfirmationButtons as hideCutConfirmationButtonsController,
  openUserSimplexConfigurator as openUserSimplexConfiguratorController,
  reconstructCompleteProblemFromHistory as reconstructCompleteProblemFromHistoryController,
  refreshAvailableCuts as refreshAvailableCutsController,
  showCutConfirmationButtons as showCutConfirmationButtonsController,
  showCuttingPlanePreview as showCuttingPlanePreviewController,
  showOptimalSolutionCongratulations as showOptimalSolutionCongratulationsController,
  validateUserSimplexData as validateUserSimplexDataController,
  type CutWorkflowEngineContext,
} from './engine/cut-workflow-controller'
import {
  addIntegerPointsWithOptimalSolution as addIntegerPointsWithOptimalSolutionController,
  animate as animateRendererController,
  calculateCurrentPolyhedronVolume as calculateCurrentPolyhedronVolumeController,
  clearVisualization as clearVisualizationController,
  createAtmosphericParticles as createAtmosphericParticlesController,
  createConvexHull as createConvexHullController,
  createIntegerPointsConvexHull as createIntegerPointsConvexHullController,
  createSimpleBoundingBox as createSimpleBoundingBoxController,
  dispose as disposeRendererController,
  generateCompleteVisualization as generateCompleteVisualizationController,
  handleResize as handleResizeController,
  resetCamera as resetCameraController,
  setupCamera as setupCameraController,
  setupControls as setupControlsController,
  setupEnvironment as setupEnvironmentController,
  setupLights as setupLightsController,
  setupRenderer as setupRendererController,
  toggleAnimation as toggleAnimationController,
  type RendererEngineContext,
} from './engine/renderer-runtime'
import { PerformanceProbe, RenderScheduler, ResourceCounters } from './engine/render-runtime-metrics'
import {
  clearStaleData as clearStaleDataSessionController,
  clearUserCuts as clearUserCutsSessionController,
  createGameProblem as createGameProblemSessionController,
  endGame as endGameSessionController,
  generateCutsForProblem as generateCutsForProblemSessionController,
  generateNewProblem as generateNewProblemSessionController,
  generateStrongerCuts as generateStrongerCutsSessionController,
  handleProblemUpdate as handleProblemUpdateSessionController,
  loadCutsLimitFromStorage as loadCutsLimitFromStorageSessionController,
  refreshCutButtons as refreshCutButtonsSessionController,
  runAlgorithmWithProblem as runAlgorithmWithProblemSessionController,
  saveCutsLimitToStorage as saveCutsLimitToStorageSessionController,
  setupCutsLimitConfiguration as setupCutsLimitConfigurationSessionController,
  setupEventListeners as setupEventListenersSessionController,
  setupUserCutEventListeners as setupUserCutEventListenersSessionController,
  toggleAllPanels as toggleAllPanelsSessionController,
  updateCutsHistoryPanel as updateCutsHistoryPanelSessionController,
  updateCutsLimit as updateCutsLimitSessionController,
  updateUI as updateUISessionController,
  type SessionEngineContext,
} from './engine/game-session-controller'
import { initializeVisualEffects } from './effects'
import { mathRenderer } from './math-panel-adapter'
import { customProblemEditor } from './custom-editor'
import { logger } from '@/app/logger'
import { appBus } from '@/app/app-events'
import { createCamera } from '@/graphics/camera'

logger.log('[Main] Custom problem editor imported:', customProblemEditor)

export class GeometricSculptor {
  private scene!: THREE.Scene
  private camera!: THREE.PerspectiveCamera
  private renderer!: THREE.WebGLRenderer
  private labelRenderer!: CSS2DRenderer
  private controls!: OrbitControls
  private polyhedronGroup: THREE.Group | null = null
  private polyManager!: PolyhedronManager
  private cutService!: CutService
  private renderScheduler!: RenderScheduler
  private performanceProbe!: PerformanceProbe
  private resourceCounters!: ResourceCounters
  private currentProblem: IntegerProblem | null = null
  private originalProblem: IntegerProblem | null = null
  private originalCustomProblem: IntegerProblem | null = null
  private availableCuts: CanonicalCut[] = []
  private cutsRemaining = 10
  private customCutsLimit = 15
  private isAnimationPlaying = false

  private pendingCut: CanonicalCut | null = null
  private pendingCutIndex = -1
  private pendingSimplexConfig: {
    baseCut: CanonicalCut
    fVals: number[]
    rays: number[][]
  } | null = null
  private currentCuttingPlane: {
    visualization: THREE.Mesh
    wireframe: THREE.LineSegments
    crossSection?: THREE.Object3D
  } | null = null

  private cutsHistory: CutHistoryItem[] = []
  private initialCutsGenerated = false
  private initialObjectiveValue = 0
  private initialIPOptimalValue = 0
  private initialIPOptimalSolution: number[] = [0, 0, 0]
  private initialVolume = 0
  private cutTypeCounters: Map<string, number> = new Map()

  private bnbManager: BnBManager | null = null
  private bnbActive = false
  private branchOverlays: THREE.Object3D[] = []
  private appBusUnsubscribers: Array<() => void> = []
  private animationFrameId: number | null = null
  private isDisposed = false
  private boundWindowResizeHandler: (() => void) | null = null
  private boundWindowKeydownHandler: ((event: KeyboardEvent) => void) | null = null

  constructor() {
    logger.log('GeometricSculptor constructor called')
    this.loadCutsLimitFromStorage()
  }

  public initialize(): void {
    if (this.isDisposed) {
      throw new Error('Cannot initialize a disposed GeometricSculptor instance')
    }
    this.initializeGame()
  }

  private initializeGame(): void {
    logger.log('Initializing game')

    this.scene = new THREE.Scene()
    this.camera = createCamera()
    this.renderer = new THREE.WebGLRenderer({ antialias: true })

    this.setupRenderer()
    this.setupCamera()
    this.setupControls()
    this.setupLights()
    this.setupEnvironment()
    this.setupEventListeners()
    this.setupUserCutEventListeners()
    this.createAtmosphericParticles()
    this.setupCutsLimitConfiguration()
    this.setupBnBIntegration()

    logger.log('[Main] mathRenderer imported:', mathRenderer)
    logger.log('[Main] mathRenderer type:', typeof mathRenderer)

    mathRenderer.setOnProblemUpdated((problem: IntegerProblem) => {
      this.handleProblemUpdate(problem)
    })

    logger.log('[Main] mathRenderer callback set up')

    initializeVisualEffects(this.scene, this.renderer, () => {
      this.renderScheduler?.invalidate?.('effects-frame')
    })
    this.scene.userData.camera = this.camera

    this.createGameProblem()
    this.updateBnBStartAvailability()
    this.animate()
  }

  private setupRenderer(): void {
    setupRendererController(this.asRendererContext())
  }

  private setupCamera(): void {
    setupCameraController(this.asRendererContext())
  }

  private setupControls(): void {
    setupControlsController(this.asRendererContext())
  }

  private setupLights(): void {
    setupLightsController(this.asRendererContext())
  }

  private setupEnvironment(): void {
    setupEnvironmentController(this.asRendererContext())
  }

  private createAtmosphericParticles(): void {
    createAtmosphericParticlesController(this.asRendererContext())
  }

  private setupBnBIntegration(): void {
    setupBnBIntegrationController(this.asBnBVisualizationContext())
  }

  private renderBnBNode(node: BnBNode): void {
    renderBnBNodeController(this.asBnBVisualizationContext(), node)
  }

  private clearBranchOverlays(): void {
    clearBranchOverlaysController(this.asBnBVisualizationContext())
  }

  private getBnBMaxNodesFromStorage(): number {
    try {
      return Math.max(1, parseInt(localStorage.getItem('bnb-max-nodes') || '7'))
    } catch {
      return 7
    }
  }

  private storeBnBMaxNodes(n: number): void {
    try {
      localStorage.setItem('bnb-max-nodes', String(n))
    } catch {
      // noop
    }
  }

  private updateBnBStartAvailability(): void {
    // React StatusBar owns Branch & Bound button state and behavior.
  }

  private asRendererContext(): RendererEngineContext {
    return this as unknown as RendererEngineContext
  }

  private asSessionContext(): SessionEngineContext {
    return this as unknown as SessionEngineContext
  }

  private asBnBVisualizationContext(): BnBVisualizationEngineContext {
    return this as unknown as BnBVisualizationEngineContext
  }

  private asCutWorkflowContext(): CutWorkflowEngineContext {
    return this as unknown as CutWorkflowEngineContext
  }

  private async createGameProblem(): Promise<void> {
    await createGameProblemSessionController(this.asSessionContext())
  }

  private setupEventListeners(): void {
    setupEventListenersSessionController(this.asSessionContext())
  }

  private toggleAllPanels(): void {
    toggleAllPanelsSessionController(this.asSessionContext())
  }

  private handleResize(): void {
    handleResizeController(this.asRendererContext())
  }

  private resetCamera(): void {
    resetCameraController(this.asRendererContext())
  }

  private toggleAnimation(): void {
    toggleAnimationController(this.asRendererContext())
    uiManager.showNotification(`Animation ${this.isAnimationPlaying ? 'Resumed' : 'Paused'}`, 'info')
  }

  private async applyCut(cutIndex: number): Promise<void> {
    await applyCutController(this.asCutWorkflowContext(), cutIndex)
  }

  private showCuttingPlanePreview(cut: CanonicalCut): void {
    showCuttingPlanePreviewController(this.asCutWorkflowContext(), cut)
  }

  private showCutConfirmationButtons(): void {
    showCutConfirmationButtonsController(this.asCutWorkflowContext())
  }

  private async confirmCut(isSimplex = false): Promise<void> {
    await confirmCutController(this.asCutWorkflowContext(), isSimplex)
  }

  private cancelCut(isSimplex = false): void {
    cancelCutController(this.asCutWorkflowContext(), isSimplex)
  }

  private handleCutMuChange(mu: number): void {
    handleCutMuChangeController(this.asCutWorkflowContext(), mu)
  }

  private cleanupCuttingPlane(): void {
    cleanupCuttingPlaneController(this.asCutWorkflowContext())
  }

  private hideCutConfirmationButtons(): void {
    hideCutConfirmationButtonsController(this.asCutWorkflowContext())
  }

  private async executeCut(cut: CanonicalCut, originalVolume: number): Promise<void> {
    await executeCutController(this.asCutWorkflowContext(), cut, originalVolume)
  }

  private calculateCurrentPolyhedronVolume(): number {
    return calculateCurrentPolyhedronVolumeController(this.asRendererContext())
  }

  private generateStrongerCuts(): CanonicalCut[] {
    return generateStrongerCutsSessionController(this.asSessionContext())
  }

  private async generateNewProblem(): Promise<void> {
    await generateNewProblemSessionController(this.asSessionContext())
  }

  private endGame(): void {
    endGameSessionController(this.asSessionContext())
  }

  private updateUI(): void {
    updateUISessionController(this.asSessionContext())
  }

  private animate(): void {
    animateRendererController(this.asRendererContext())
  }

  private handleProblemUpdate(problem: IntegerProblem): void {
    handleProblemUpdateSessionController(this.asSessionContext(), problem)
  }

  private clearVisualization(): void {
    clearVisualizationController(this.asRendererContext())
  }

  private runAlgorithmWithProblem(problem: IntegerProblem): void {
    runAlgorithmWithProblemSessionController(this.asSessionContext(), problem)
  }

  private generateCutsForProblem(problem: IntegerProblem): void {
    generateCutsForProblemSessionController(this.asSessionContext(), problem)
  }

  private reconstructCompleteProblemFromHistory(): IntegerProblem {
    return reconstructCompleteProblemFromHistoryController(this.asCutWorkflowContext())
  }

  private async refreshAvailableCuts(): Promise<void> {
    await refreshAvailableCutsController(this.asCutWorkflowContext())
  }

  private showOptimalSolutionCongratulations(optimalValue: number): void {
    showOptimalSolutionCongratulationsController(this.asCutWorkflowContext(), optimalValue)
  }

  private generateCompleteVisualization(problem: IntegerProblem): void {
    generateCompleteVisualizationController(this.asRendererContext(), problem)
  }

  private createConvexHull(problem: IntegerProblem): void {
    createConvexHullController(this.asRendererContext(), problem)
  }

  private addIntegerPointsWithOptimalSolution(problem: IntegerProblem): void {
    addIntegerPointsWithOptimalSolutionController(this.asRendererContext(), problem)
  }

  private createIntegerPointsConvexHull(integerPoints: THREE.Vector3[]): void {
    createIntegerPointsConvexHullController(this.asRendererContext(), integerPoints)
  }

  private createSimpleBoundingBox(points: THREE.Vector3[]): void {
    createSimpleBoundingBoxController(this.asRendererContext(), points)
  }

  private updateCutsHistoryPanel(): void {
    updateCutsHistoryPanelSessionController(this.asSessionContext())
  }

  private setupUserCutEventListeners(): void {
    setupUserCutEventListenersSessionController(this.asSessionContext())
  }

  private refreshCutButtons(): void {
    refreshCutButtonsSessionController(this.asSessionContext())
  }

  private loadCutsLimitFromStorage(): void {
    loadCutsLimitFromStorageSessionController(this.asSessionContext())
  }

  private saveCutsLimitToStorage(limit: number): void {
    saveCutsLimitToStorageSessionController(this.asSessionContext(), limit)
  }

  private setupCutsLimitConfiguration(): void {
    setupCutsLimitConfigurationSessionController(this.asSessionContext())
  }

  private updateCutsLimit(newLimit: number): void {
    updateCutsLimitSessionController(this.asSessionContext(), newLimit)
  }

  public clearUserCuts(): void {
    clearUserCutsSessionController(this.asSessionContext())
  }

  public getPerformanceSummaries(): Record<string, { count: number; p50: number; p95: number; max: number; avg: number }> {
    return this.performanceProbe?.getAllSummaries?.() ?? {}
  }

  public getResourceCountersSnapshot(): { activeOverlays: number; createdGeometries: number; disposedGeometries: number; createdMaterials: number; disposedMaterials: number } {
    return this.resourceCounters?.snapshot?.() ?? {
      activeOverlays: 0,
      createdGeometries: 0,
      disposedGeometries: 0,
      createdMaterials: 0,
      disposedMaterials: 0,
    }
  }

  public invalidateRender(reason: string): void {
    this.renderScheduler?.invalidate(reason)
  }

  public async requestCutPreview(cutIndex: number): Promise<void> {
    await this.applyCut(cutIndex)
  }

  public async requestConfirmCut(): Promise<void> {
    await this.confirmCut(false)
  }

  public requestCancelCut(): void {
    this.cancelCut(false)
  }

  public hasActiveCutPreview(): boolean {
    return this.currentCuttingPlane !== null
  }

  public requestBnBStart(): void {
    appBus.emit('bnb-start-requested', undefined)
  }

  public requestBnBBranch(varIndex: number, mode: 'one' | 'both' = 'both', side?: 'le' | 'ge', nodeId?: string): void {
    appBus.emit('bnb-branch-requested', { varIndex, mode, side, nodeId })
  }

  public requestBnBSelect(nodeId: string): void {
    appBus.emit('bnb-select-requested', { nodeId })
  }

  public requestBnBBacktrack(nodeId: string): void {
    appBus.emit('bnb-backtrack-requested', { nodeId })
  }

  public requestBnBReset(): void {
    appBus.emit('bnb-reset-requested', {})
  }

  public getBnBNodeIds(): string[] {
    const manager = this.bnbManager
    const state = manager?.getState()
    return state ? Array.from(state.nodes.keys()) : []
  }

  public getCurrentBnBNodeId(): string | null {
    const manager = this.bnbManager
    const state = manager?.getState()
    return state ? state.currentNodeId : null
  }

  private clearStaleData(): void {
    clearStaleDataSessionController(this.asSessionContext())
  }

  private validateUserSimplexData(cut: CanonicalCut, problem: IntegerProblem): boolean {
    return validateUserSimplexDataController(this.asCutWorkflowContext(), cut, problem)
  }

  private createCutWithMuValue(
    baseCut: CanonicalCut,
    muValue: number,
    fVals: number[],
    rays: number[][],
  ): CanonicalCut {
    return createCutWithMuValueController(this.asCutWorkflowContext(), baseCut, muValue, fVals, rays)
  }

  private openUserSimplexConfigurator(cut: CanonicalCut, cutIndex: number): void {
    openUserSimplexConfiguratorController(this.asCutWorkflowContext(), cut, cutIndex)
  }

  public dispose(): void {
    clearBnBCachesController()
    disposeRendererController(this.asRendererContext())
  }
}
